import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * A full-screen layer that is actually full-screen.
 *
 * `position: fixed` promises "relative to the viewport" and on iOS it does not
 * keep that promise inside a scrolling container. Every one of these screens
 * was rendered from somewhere inside a `.scroll-area`, so the browser laid them
 * out against *that box* rather than the window — which put the verify screen
 * neatly between the app's own header and the tab bar, with its back button and
 * its confirm button outside the visible strip.
 *
 * From the outside it looked like the buttons were missing. They were on
 * screen, in the sense that they existed; they were simply not anywhere a
 * finger could reach.
 *
 * A portal to `document.body` sidesteps the whole question: nothing above it
 * can scroll, transform or clip, so `inset-0` means the screen no matter which
 * component happened to render it.
 */

/**
 * Layers, named rather than numbered.
 *
 * These open on top of each other — a payment card over the list, its history
 * over the card, the original document over everything, because looking at the
 * paper is what settles an argument with any of the others. Writing the numbers
 * at each call site is how two of them end up equal and one becomes unreachable.
 */
export const LAYER = {
  screen: 'z-50',
  overScreen: 'z-[55]',
  sheet: 'z-[60]',
  document: 'z-[70]',
} as const

/**
 * One lock, however many layers are open.
 *
 * Each overlay saving and restoring `body.overflow` itself works only while
 * they unmount in the order they mounted. Close the payment card while its
 * document viewer is still up and the page underneath starts scrolling again
 * with a full-screen layer over it.
 */
let locks = 0
let restoreTo = ''

function useBodyScrollLock() {
  useEffect(() => {
    if (locks === 0) {
      restoreTo = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    locks += 1
    return () => {
      locks -= 1
      if (locks === 0) document.body.style.overflow = restoreTo
    }
  }, [])
}

export function Overlay({
  children,
  className = '',
  layer = LAYER.screen,
}: {
  children: ReactNode
  className?: string
  /** Which stacking layer. Pass a LAYER value, never a bare class. */
  layer?: string
}) {
  useBodyScrollLock()

  return createPortal(
    <div className={`fixed inset-0 ${layer} flex flex-col bg-canvas ${className}`}>{children}</div>,
    document.body,
  )
}

/**
 * The same, for a sheet that rises from the bottom over a dimmed page.
 *
 * Separate rather than a flag: the two have different backgrounds and different
 * alignment, and a component that is two things depending on a boolean is how
 * one of them quietly stops being right.
 */
export function SheetOverlay({ children }: { children: ReactNode }) {
  useBodyScrollLock()

  return createPortal(
    <div className={`fixed inset-0 ${LAYER.sheet} bg-ink/40 flex items-end`}>{children}</div>,
    document.body,
  )
}
